// frontend/src/components/admin/ConfirmResetUserFeedbackModal.tsx
import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useAdminFeedbackResetPreview,
  useAdminFeedbackResetUser,
} from "@/hooks/useAdminFeedbackReset";
import type { AdminUser } from "@/hooks/useAdminUsers";

interface Props {
  user: AdminUser | null;
  open: boolean;
  onClose: () => void;
  onReset?: (deleted: number) => void;
}

export function ConfirmResetUserFeedbackModal({
  user,
  open,
  onClose,
  onReset,
}: Props) {
  const preview = useAdminFeedbackResetPreview(open && user ? user.id : null);
  const reset = useAdminFeedbackResetUser();

  useEffect(() => {
    if (!open) reset.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (reset.isSuccess && reset.data && onReset) {
      onReset(reset.data.deleted);
    }
  }, [reset.isSuccess, reset.data, onReset]);

  function handleReset() {
    if (!user) return;
    reset.mutate(user.id);
  }

  const loading = open && preview.isLoading;
  const p = preview.data;
  const hasRows = !!p && p.total > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="size-4" />
            Reset feedback events
            {user ? <span className="text-sm text-muted-foreground">
              for {user.email}
            </span> : null}
          </DialogTitle>
          <DialogDescription>
            This will permanently delete every feedback event this user has
            logged. Cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-4 text-sm text-muted-foreground">Loading preview…</div>
        )}

        {!loading && p && !hasRows && (
          <div className="py-4 text-sm text-muted-foreground">
            This user has no feedback events to reset.
          </div>
        )}

        {!loading && hasRows && (
          <div className="rounded border p-3 text-sm">
            <div className="font-medium mb-2">
              {p.total} feedback event{p.total === 1 ? "" : "s"} will be deleted
            </div>
            <ul className="space-y-0.5 text-muted-foreground">
              {p.by_signal_type.map((row) => (
                <li key={row.signal_type} className="flex justify-between">
                  <span className="font-mono text-xs">{row.signal_type}</span>
                  <span className="tabular-nums">{row.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {reset.isError && (
          <div
            role="alert"
            className="rounded border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive"
          >
            {(reset.error as Error)?.message ?? "Reset failed."}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={reset.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleReset}
            disabled={!hasRows || reset.isPending || loading}
          >
            {reset.isPending ? "Resetting…" : "Reset feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
